/* ============================================================
   map-embed.jsx — boots the embedded Herradura interactive map on
   /en/herradura/ and /es/herradura/.
   Reuses the web-app components loaded before this file:
   MapScreen (OSD viewer + pins + category filter) and SidePanel
   (hotspot detail), locked to the HE (Herradura) region.
   Language is fixed by the page's <html lang>.
   ============================================================ */
/* global React, ReactDOM, MapScreen, SidePanel */

// Same tracker as /phone/maps.html — analytics land in the standard pipeline.
if (!window.trackEvent) {
  window.trackEvent = function (data) {
    try {
      let uid = localStorage.getItem('crmaps_uid');
      if (!uid) { uid = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`; localStorage.setItem('crmaps_uid', uid); }
      let sid = sessionStorage.getItem('crmaps_sid');
      if (!sid) { sid = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`; sessionStorage.setItem('crmaps_sid', sid); }
      const attribution = typeof window.CRMapsAnalyticsContext === 'function' ? window.CRMapsAnalyticsContext() : {};
      const payload = JSON.stringify({ ...data, ...attribution, userId: uid, sessionId: sid, ts: new Date().toISOString() });
      if (navigator.sendBeacon) { navigator.sendBeacon('/api/analytics/track', new Blob([payload], { type: 'application/json' })); }
      else { fetch('/api/analytics/track', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {}); }
    } catch {}
  };
}

const HE_MAP_COPY = {
  en: { selectMap: 'Select map', filterCategory: 'Filter by category', loadingHotspots: 'Loading hotspots...' },
  es: { selectMap: 'Selecciona mapa', filterCategory: 'Filtrar por categoría', loadingHotspots: 'Cargando hotspots...' },
};

const HE_PAGE_LANG = document.documentElement.lang === 'es' ? 'es' : 'en';
const HE_REGION = { id: 'HE' };
// Deep link desde la ficha comercial («Cerca de acá»): /es|en/<destino>/?client=<id>#interactive-map
// centra el mapa en ese cliente y abre su panel (MapScreen.focusHotspot, 2026-09-14).
const HE_FOCUS_CLIENT = (() => {
  try { return new URLSearchParams(window.location.search).get('client') || null; } catch { return null; }
})();

function HerraduraMapEmbed() {
  const [activeHs, setActiveHs] = React.useState(null);

  function openHotspot(hs) {
    setActiveHs(hs);
    if (window.trackEvent) window.trackEvent({ type: 'popup_open', company: hs.name, clientId: hs.id || '', mapCode: hs.region || '' });
  }

  React.useEffect(() => {
    if (window.trackEvent) window.trackEvent({ type: 'map_view', mapCode: 'HE' });
  }, []);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setActiveHs(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Like the home visor: wheel over the side panel scrolls ONLY the panel
  // content — never the page. Scrolling anywhere else keeps working.
  React.useEffect(() => {
    const panel = document.querySelector('.jb-viewer-stage .hp-panel');
    if (!panel) return;
    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const scroller = panel.querySelector('.hp-scroll');
      if (!scroller) return;
      const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      scroller.scrollTop += delta;
    };
    panel.addEventListener('wheel', onWheel, { passive: false });
    return () => panel.removeEventListener('wheel', onWheel);
  }, []);

  return (
    <div className="jb-viewer-stage">
      <MapScreen
        lang={HE_PAGE_LANG}
        onLanguageChange={() => {}}
        onHotspotClick={openHotspot}
        activeHotspot={activeHs?.id}
        onTideClick={() => {}}
        targetRegion={HE_REGION}
        focusHotspot={HE_FOCUS_CLIENT}
        copy={HE_MAP_COPY[HE_PAGE_LANG]}
        onMapChange={() => {}}
      />
      <SidePanel
        hs={activeHs}
        lang={HE_PAGE_LANG}
        onClose={() => setActiveHs(null)}
        onSwitchMap={() => {}}
        useStaticTiles={true}
      />
    </div>
  );
}

// Móvil (<768px): el visor no se monta; la sección muestra en su lugar el botón
// .jb-map__cta, que abre la web-app directo en este mapa (?map=). Si la ventana
// pasa a escritorio (rotación, resize) el visor se monta recién ahí, una sola vez.
const herraduraMapRootEl = document.getElementById('herraduraMapRoot');
const herraduraDesktopMq = window.matchMedia('(min-width: 768px)');
let herraduraMapMounted = false;
function mountHerraduraMapEmbed() {
  if (herraduraMapMounted || !herraduraMapRootEl || !herraduraDesktopMq.matches) return;
  herraduraMapMounted = true;
  ReactDOM.createRoot(herraduraMapRootEl).render(<HerraduraMapEmbed />);
}
mountHerraduraMapEmbed();
if (herraduraDesktopMq.addEventListener) herraduraDesktopMq.addEventListener('change', mountHerraduraMapEmbed);

// El botón móvil conserva el deep link del cliente («Cerca de acá»): ?client= pasa a la app.
if (HE_FOCUS_CLIENT) {
  const herraduraMapCta = document.querySelector('.jb-map__cta a[href]');
  if (herraduraMapCta) herraduraMapCta.href += '&client=' + encodeURIComponent(HE_FOCUS_CLIENT);
}
